import React from "react";
import draftToHtml from "draftjs-to-html";

/**
 * Public component rendering safe rich-text HTML across Lexical, Draft.js, and plain text.
 */
export const SafeRichTextRenderer = ({ text, className }) => {
    const html = renderRichTextToSafeHtml(text);
    if (!html) {
        return null;
    }
    return (
        <div
            className={className || undefined}
            dangerouslySetInnerHTML={{ __html: html }}
        />
    );
};

export default SafeRichTextRenderer;

/**
 * Parses and sanitizes raw rich text into an XSS-safe HTML string.
 *
 * @param {string|null|undefined} rawText
 * @returns {string} Safe HTML string or empty string
 */
export const renderRichTextToSafeHtml = (rawText) => {
    if (!rawText || typeof rawText !== "string" || !rawText.trim()) {
        return "";
    }
    const html = toRawHtml(rawText);
    return sanitizeHtml(html);
};

// --- Private Implementation Helpers ---

const toRawHtml = (rawText) => {
    try {
        const parsed = JSON.parse(rawText);
        return convertParsedJson(parsed, rawText);
    } catch {
        return handlePlainText(rawText);
    }
};

const convertParsedJson = (parsed, rawText) => {
    if (parsed?.root && Array.isArray(parsed.root.children)) {
        return serializeLexicalNodes(parsed.root.children);
    }
    if (Array.isArray(parsed?.blocks)) {
        return draftToHtml(parsed);
    }
    if (typeof parsed === "string") {
        return handlePlainText(parsed);
    }
    return handlePlainText(rawText);
};

const handlePlainText = (text) => {
    const trimmed = text.trim();
    if (/<[a-z][\s\S]*>/i.test(trimmed)) {
        return trimmed;
    }
    return `<p>${escapeHtml(trimmed)}</p>`;
};

const serializeLexicalNodes = (nodes) => {
    if (!Array.isArray(nodes)) {
        return "";
    }
    return nodes.map(serializeLexicalNode).join("");
};

const serializeLexicalNode = (node) => {
    if (!node) {
        return "";
    }
    const children = serializeLexicalNodes(node.children);
    switch (node.type) {
        case "paragraph":
            return `<p>${children}</p>`;
        case "heading": {
            const tag = node.tag || "h1";
            return `<${tag}>${children}</${tag}>`;
        }
        case "quote":
            return `<blockquote>${children}</blockquote>`;
        case "list": {
            const listTag = node.listType === "number" ? "ol" : "ul";
            return `<${listTag}>${children}</${listTag}>`;
        }
        case "listitem":
            return `<li>${children}</li>`;
        case "link":
            return renderLink(node, children);
        case "linebreak":
            return "<br />";
        case "text":
            return formatText(node.text || "", node.format || 0);
        default:
            return children;
    }
};

const renderLink = (node, children) => {
    if (isSafeUrl(node.url)) {
        return `<a href="${escapeHtml(node.url)}" target="_blank" rel="noopener noreferrer">${children}</a>`;
    }
    return `<a>${children}</a>`;
};

const formatText = (text, format) => {
    let result = escapeHtml(text);
    if (!format) {
        return result;
    }
    if (format & 16) {
        result = `<code>${result}</code>`;
    }
    if (format & 8) {
        result = `<u>${result}</u>`;
    }
    if (format & 4) {
        result = `<s>${result}</s>`;
    }
    if (format & 2) {
        result = `<em>${result}</em>`;
    }
    if (format & 1) {
        result = `<strong>${result}</strong>`;
    }
    return result;
};

const isSafeUrl = (url) => {
    if (!url || typeof url !== "string") {
        return false;
    }
    const lower = url.trim().toLowerCase();
    return lower.startsWith("http://") || lower.startsWith("https://") || lower.startsWith("mailto:");
};

const sanitizeHtml = (html) => {
    if (!html) {
        return "";
    }
    let clean = html.replace(/<\s*(script|style|iframe)[\s\S]*?<\s*\/\s*\1\s*>/gi, "");
    clean = clean.replace(/<\s*(script|style|iframe)[^>]*>/gi, "");
    clean = clean.replace(/\son[a-zA-Z]+\s*=\s*(?:'[^']*'|"[^"]*"|[^\s>]+)/gi, "");
    clean = clean.replace(/(href|src)\s*=\s*["']\s*(?:javascript|data|vbscript):[^"']*["']/gi, '$1="#"');
    return clean;
};

const escapeHtml = (str) => {
    return String(str)
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/"/g, "&quot;")
        .replace(/'/g, "&#39;");
};
